import { downloadCSV } from "@/lib/downloadCSV";
import { throwError } from "@/lib/throwError";
import { get, patch, post } from "@/services/axiosUitils";
import {
  ColumnMapping,
  ExportDataRequest,
  ImportDataRequest,
  RunImportResponse,
  UpdateColumnMappingRequest,
  UpdateColumnMappingResponse,
} from "@/utils/types/dataOperator";

class DataOperationsService {
  exportData = async (
    entity: string,
    request: ExportDataRequest
  ): Promise<string> => {
    if (!entity || !request) {
      throwError("Entity and request are required", "DataOperationsService");
    }
    const response = await post(`/export-data/${entity}/`, request, {
      responseType: "text",
    });

    // Handle CSV data download
    if (typeof response === "string" && response.includes(",")) {
      const filename = `${entity}_export_${new Date().toISOString().split("T")[0]}`;
      downloadCSV(response, filename);
    }

    return response;
  };

  importData = async (
    entity: string,
    request: ImportDataRequest
  ): Promise<any> => {
    if (!entity || !request) {
      throwError("Entity and request are required", "DataOperationsService");
    }
    const formData = new FormData();
    formData.append("document_id", request.document_id);
    formData.append("import_type", request.import_type);

    return await post(`/import-data/create/${entity}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  runImport = async (documentId: string): Promise<RunImportResponse> => {
    if (!documentId) {
      throwError("Document ID is required", "DataOperationsService");
    }
    return await post<RunImportResponse>(`/run-import/${documentId}/`);
  };

  updateColumnMapping = async (
    documentId: string,
    request: UpdateColumnMappingRequest
  ): Promise<UpdateColumnMappingResponse> => {
    if (!documentId || !request) {
      throwError(
        "Document ID and request are required",
        "DataOperationsService"
      );
    }
    return await patch<UpdateColumnMappingResponse>(
      `/data-import/${documentId}/update-column-mapping/`,
      request
    );
  };

  // Complete import workflow method
  completeImportWorkflow = async (
    documentId: string,
    columnMapping: ColumnMapping[]
  ): Promise<{
    mappingResult: UpdateColumnMappingResponse;
    importResult: RunImportResponse;
  }> => {
    if (!documentId) {
      throwError("Document ID is required", "DataOperationsService");
    }

    // First update column mapping
    const mappingResult = await this.updateColumnMapping(documentId, {
      column_mapping: columnMapping,
    });

    // Then run import
    const importResult = await this.runImport(documentId);

    return { mappingResult, importResult };
  };

  downloadErrorFile = async (importId: string): Promise<any> => {
    if (!importId) {
      throwError("File URL is required", "DataOperationsService");
    }
    return await get(`/download-error-file/?import_id=${importId}/`);
  };
}

const instance = new DataOperationsService();
export default instance;
